import { supabaseClient } from "@/lib/supabase";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";

export interface FormMetadataData {
    form: {
        form_id: string | null;
        form_name: string | null;
        form_description: string | null;
        form_instructions: string | null;
        overview_text: string | null;
        status: string | null;
        is_published: boolean | null;
        is_licensed: boolean | null;
        scoring: string | null;
        scoring_methodology: string | null;
        prevalence: string | null;
        time_to_complete: number | null;
        number_of_pages: number | null;
        number_of_questions: number | null;
        medical_specialties: string[] | null;
        orthopaedic_areas: string[] | null;
        indications: string[] | null;
        limitations: string | null;
        meaningful_change_threshold: string | null;
        interpretation: any;
        subscales: any;
        copyright_info: string | null;
        developer_info: string | null;
        price: number | null;
        created_at: string | null;
    };
    research_papers: {
        paper_id: string;
        title: string;
        authors: string;
        journal: string;
        year: number;
        abstract: string | null;
        doi: string | null;
        url: string | null;
        citation_count: number | null;
        impact_factor: number | null;
        is_peer_reviewed: boolean | null;
        study_type: string | null;
        sample_size: number | null;
        population: string | null;
        country: string | null;
        language: string | null;
    }[];
}

export async function selectFormMetadata(formId: string): Promise<FormMetadataData> {
    // Fetch form details
    const { data: formData, error: formError } = await supabaseClient
        .from("forms_view")
        .select("*")
        .eq("form_id", formId)
        .single();

    if (formError) throw formError;

    // Fetch research papers that reference this form
    const { data: papersData, error: papersError } = await supabaseClient
        .from("form_in_literature")
        .select(`
			paper_id,
			research_papers (
				paper_id,
				title,
				authors,
				journal,
				year,
				abstract,
				doi,
				url,
				citation_count,
				impact_factor,
				is_peer_reviewed,
				study_type,
				sample_size,
				population,
				country,
				language
			)
		`)
        .eq("form_id", formId);

    if (papersError) throw papersError;

    // Transform the data to flatten the structure
    const research_papers = papersData?.map((item) => item.research_papers).filter(Boolean) || [];

    return {
        form: formData,
        research_papers: research_papers as FormMetadataData["research_papers"],
    };
}

export const selectFormMetadataQueryOptions = (formId: string) =>
    queryOptions({
        queryKey: ["form-metadata", formId],
        queryFn: () => selectFormMetadata(formId),
    });

export function useFormMetadata(formId: string) {
    return useSuspenseQuery(selectFormMetadataQueryOptions(formId));
}
